import React, { ReactNode, useCallback, useState, useEffect } from 'react'

import MuiTabs, { tabsClasses } from '@mui/material/Tabs'
import MuiTab from '@mui/material/Tab'
import Box from '@mui/material/Box'

// TODO: do we need both of these styled imports?
import { styled } from '@mui/material/styles'
import styledComponent from 'styled-components'

import TabPanel from 'components/Tabs/TabPanel'
import KeyboardAccessible from 'components/KeyboardAccessible'

import { color } from 'styles/colors'

import { screenSize } from 'styles/shared'

export interface Tab {
  key: string
  header: ReactNode
  details: ReactNode
  alerts?: number
  badge?: number
}

type Props = {
  activeTabIndex?: number
  tabItems: Array<Tab>
  onSelectTabIndexCallback?: (index: number) => void
  variant?: string
  // TODO: regarding tabpage, Tabs is a general component and should not contain logic for specific pages,
  // but instead should be configurable by those pages.
  // Instead of passing in a "tabpage" prop, there should either be a separate component for each use case,
  // or the styles should be configurable outside of this of this component.
  tabpage?: 'device' | 'site_message' | 'others'
}

const Tabs = ({ tabItems, onSelectTabIndexCallback = () => {}, activeTabIndex = 0, variant = 'scrollable', tabpage = 'others' }: Props) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(activeTabIndex)

  useEffect(() => {
    setSelectedTabIndex(activeTabIndex)
  }, [activeTabIndex])

  const setSelectedTab = useCallback((newIndex: number) => {
    if (newIndex === selectedTabIndex) return

    setSelectedTabIndex(newIndex)
    onSelectTabIndexCallback(newIndex)
  }, [selectedTabIndex, onSelectTabIndexCallback])

  const tabId = (key: string) => `tab-${key}`
  const tabPanelId = (key: string) => `tab-panel-${key}`

  return (
    <S.TabContainer tabpage={tabpage}>
      <S.Tabs
        value={selectedTabIndex}
        variant={variant}
        scrollButtons="auto"
        allowScrollButtonsMobile
        tabpage={tabpage}
      >
        {tabItems.map((item, index) => (
          <S.KeyboardAccessible
            key={item.key}
            onClick={() => setSelectedTab(index)}
            tabpage={tabpage}
          >
            <S.Tab
              tabIndex={-1}
              tabpage={tabpage}
              alerts={item.alerts}
              disableRipple
              label={
                <>
                  {(item.badge !== undefined && item.badge >= 0)
                    ? (
                      <S.Header>
                        <S.Box selected={activeTabIndex === index}>{item.badge}</S.Box>
                        {item.header}
                      </S.Header>
                    )
                    : <>{item.header}</>
                  }
                </>
              }
              id={tabId(item.key)}
              aria-controls={tabPanelId(item.key)}
              selected={activeTabIndex === index}
            />
          </S.KeyboardAccessible>
        ))}
      </S.Tabs>
      {tabItems.map((item, index) => <S.TabPanel key={item.key} hidden={index !== selectedTabIndex} id={tabPanelId(item.key)} ariaLabelledby={tabId(item.key)} >{item.details}</S.TabPanel>)}
    </S.TabContainer>
  )
}

export default Tabs

const S = {
  TabContainer: styledComponent.div.withConfig({
    shouldForwardProp: (prop) => !['tabpage'].includes(prop)
  })<{ tabpage: string }>`
    display: grid;
    background-color: ${({ tabpage }) => tabpage === 'device' && !screenSize.isSmall() ? color('activeTabBackground') : color('paneBackground')};
    border-radius: 5px;
  `,
  TabPanel: styled(TabPanel)`
    width: 100%;
    overflow: auto;
  `,
  Tabs: styled(MuiTabs, {
    shouldForwardProp: (prop: string) => !['tabpage'].includes(prop)
  })<{ tabpage: string }>`
    background-color: ${color('paneBackground')};
    min-height: 42px;
    ${({ tabpage }) => (tabpage !== 'device' && tabpage !== 'site_message' && `
      border-bottom: 1px solid ${color('border')};
    `)}
    & .MuiTabs-indicator {
      ${({ tabpage }) => (tabpage !== 'device' && `
        border-bottom: 1px solid ${color('border')}
      `)}

      background-color: ${({ tabpage }) => (tabpage === 'device' ? 'transparent' : 'activeTabHighlight')};
    }

    ${() => `
      & .${tabsClasses.scrollButtons} {
        align-self: center;

        &.Mui-disabled {
          opacity: 0.3;
        }

        & svg {
          font-size: 40px;
        }
      }
    `}
  `,
  KeyboardAccessible: styledComponent(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['tabpage'].includes(prop)
  })<{ tabpage: string }>`
    padding: 2px 2px 0 2px;

    ${({ tabpage }) => (tabpage === 'device'
    ? `
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      `
    : `
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      `)}
  `,
  // TODO: tabpage is referenced in this styled component but not passed in
  Tab: styled(MuiTab, {
    shouldForwardProp: (prop: string) => !['alerts'].includes(prop)
  })<{ alerts: number, selected: boolean }>`
    font-family: Inter, sans-serif;
    color: ${color('primaryText')};
    cursor: pointer;
    padding: 0px;
    margin: 0;
    min-width: 110px;
    ${({ tabpage }) => tabpage !== 'site_message' && `
      background-color: ${({ tabpage }) => (tabpage === 'device' ? color('paneBackground') : color('inactiveTabBackground'))};
    `}
    border: ${({ tabpage }) => tabpage === 'site_message' ? 'none' : `1px solid ${color('border')}`};
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    border-bottom-width: 0px;
    min-height: 42px;
    ${({ tabpage }) => (tabpage === 'device' && `
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border-width:  1px;
    `)}

    ${({ selected }) => selected && `
      color: ${color('primaryText')};
      border-color: ${color('secondaryButtonBorder')};
      background-color: ${({ tabpage }) => (tabpage === 'device' ? color('activeTabBackground') : color('paneBackground'))};
      cursor: default;
      border-width: ${({ tabpage, alerts }) => (tabpage === 'device' && alerts ? '0px 0px 0px' : '3px 3px 0px')};
    }`}

    &:focus { outline: none; }
  `,
  Box: styled(Box)<{ selected: boolean }>`
    width: 18px;
    height: 16px;
    border-radius: 8px;
    ${({ selected }) => (selected
    ? `
        background-color: ${color('primaryButton')};
        color: ${color('primaryButtonText')}
      `
    : `
        background-color: ${color('secondaryButtonText')};
        color: ${color('secondaryButton')}        
      `)}    
  `,
  Header: styledComponent.div`
    display: flex;
    gap: 5px;
    padding: 0 15px;
  `
}
